import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
  random,
} from 'remotion';

interface Particle {
  id: number;
  x: number;
  y: number;
  angle: number;
  speed: number;
  size: number;
  color: string;
  rotationSpeed: number;
  trail: { x: number; y: number }[];
}

export const ParticleExplosionEnhanced: React.FC<{
  particleCount?: number;
  text?: string;
  backgroundColor?: string;
  explosionPower?: number;
}> = ({
  particleCount = 120,
  text = 'EXPLOSION',
  backgroundColor = '#0a0a0a',
  explosionPower = 1,
}) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  const centerX = width / 2;
  const centerY = height / 2;

  // 三阶段动画
  const phase1End = 40; // 聚合阶段
  const phase2End = 100; // 爆炸阶段
  const phase3End = 150; // 重新聚合阶段

  // 生成粒子
  const particles: Particle[] = React.useMemo(() => {
    const colors = ['#f59e0b', '#ef4444', '#ec4899', '#8b5cf6', '#06b6d4'];
    return Array.from({ length: particleCount }, (_, i) => ({
      id: i,
      x: centerX,
      y: centerY,
      angle: (i / particleCount) * Math.PI * 2,
      speed: random(`speed-${i}`) * 8 + 4,
      size: random(`size-${i}`) * 12 + 6,
      color: colors[Math.floor(random(`color-${i}`) * colors.length)],
      rotationSpeed: random(`rotation-${i}`) * 10 - 5,
      trail: [],
    }));
  }, [particleCount, centerX, centerY]);

  // 爆炸波形
  const explosionWave = spring({
    frame: frame - phase1End,
    fps,
    config: { damping: 15, stiffness: 100, mass: 0.5 },
  });

  // 文字动画
  const textSpring = spring({
    frame: frame - phase2End,
    fps,
    config: { damping: 100, stiffness: 200, mass: 0.5 },
  });

  // 计算粒子位置
  const getParticlePosition = (particle: Particle) => {
    if (frame < phase1End) {
      // 阶段 1：从外部聚合到中心
      const progress = frame / phase1End;
      const outerRadius = Math.max(width, height) * 0.6;
      const currentRadius = interpolate(progress, [0, 1], [outerRadius, 0]);
      return {
        x: centerX + Math.cos(particle.angle) * currentRadius,
        y: centerY + Math.sin(particle.angle) * currentRadius,
      };
    } else if (frame < phase2End) {
      // 阶段 2：爆炸
      const progress = (frame - phase1End) / (phase2End - phase1End);
      const distance = progress * particle.speed * 50 * explosionPower;
      return {
        x: centerX + Math.cos(particle.angle) * distance,
        y: centerY + Math.sin(particle.angle) * distance,
      };
    } else {
      // 阶段 3：重新聚合
      const progress = (frame - phase2End) / (phase3End - phase2End);
      const outerDistance = particle.speed * 50 * explosionPower;
      const currentDistance = interpolate(progress, [0, 1], [outerDistance, 0]);
      return {
        x: centerX + Math.cos(particle.angle) * currentDistance,
        y: centerY + Math.sin(particle.angle) * currentDistance,
      };
    }
  };

  return (
    <AbsoluteFill
      style={{
        backgroundColor,
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      {/* SVG 滤镜 */}
      <svg width={0} height={0} style={{ position: 'absolute' }}>
        <defs>
          <filter id="glow-enhanced">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
      </svg>

      {/* 背景爆炸光晕 */}
      {frame >= phase1End && frame < phase2End && (
        <div
          style={{
            position: 'absolute',
            width: width * 0.8,
            height: width * 0.8,
            borderRadius: '50%',
            background: `radial-gradient(circle, rgba(255, 150, 50, ${explosionWave * 0.3}) 0%, transparent 70%)`,
            opacity: explosionWave,
            transform: `scale(${1 + explosionWave * 0.5})`,
          }}
        />
      )}

      {/* 粒子 */}
      {particles.map((particle) => {
        const pos = getParticlePosition(particle);
        const opacity = interpolate(
          frame,
          [0, phase1End, phase2End, phase3End],
          [1, 1, 0.8, 1]
        );

        const scale = interpolate(
          frame,
          [0, phase1End, phase2End, phase3End],
          [0.5, 1, 1.5, 1]
        );

        const rotation = frame * particle.rotationSpeed;

        return (
          <div
            key={particle.id}
            style={{
              position: 'absolute',
              left: pos.x,
              top: pos.y,
              width: particle.size,
              height: particle.size,
              backgroundColor: particle.color,
              borderRadius: '50%',
              opacity,
              transform: `translate(-50%, -50%) scale(${scale}) rotate(${rotation}deg)`,
              filter: 'url(#glow-enhanced)',
              boxShadow: `0 0 ${particle.size}px ${particle.color}`,
            }}
          />
        );
      })}

      {/* 粒子连线 */}
      {frame >= phase2End && (
        <svg
          width={width}
          height={height}
          style={{ position: 'absolute', pointerEvents: 'none' }}
        >
          {particles.slice(0, 50).map((particle, i) => {
            if (i % 3 !== 0) return null;
            const pos = getParticlePosition(particle);
            const opacity = interpolate(
              frame,
              [phase2End, phase3End],
              [0, 0.3]
            );

            return (
              <line
                key={i}
                x1={centerX}
                y1={centerY}
                x2={pos.x}
                y2={pos.y}
                stroke={particle.color}
                strokeWidth="1"
                opacity={opacity}
              />
            );
          })}
        </svg>
      )}

      {/* 中心文字 */}
      <div
        style={{
          position: 'absolute',
          fontSize: 100,
          fontWeight: 'bold',
          color: 'white',
          fontFamily: 'Arial, sans-serif',
          letterSpacing: 10,
          opacity: textSpring,
          transform: `scale(${textSpring})`,
          textShadow: '0 0 20px rgba(255, 150, 50, 0.8)',
          zIndex: 10,
        }}
      >
        {text}
      </div>

      {/* 震动效果 */}
      {frame >= phase1End && frame < phase1End + 20 && (
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            transform: `translate(${Math.sin(frame * 2) * 5}px, ${Math.cos(frame * 2) * 5}px)`,
          }}
        />
      )}

      {/* 阶段指示器 */}
      <div
        style={{
          position: 'absolute',
          bottom: 40,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 10,
        }}
      >
        {['聚合', '爆炸', '重组'].map((label, i) => {
          const isActive =
            (i === 0 && frame < phase1End) ||
            (i === 1 && frame >= phase1End && frame < phase2End) ||
            (i === 2 && frame >= phase2End);

          return (
            <div
              key={i}
              style={{
                width: 80,
                height: 4,
                backgroundColor: isActive ? '#f59e0b' : '#333',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            />
          );
        })}
      </div>
    </AbsoluteFill>
  );
};
